class HomepageIconsSlider {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error('Homepage Icons Slider container not found:', containerId);
      return;
    }

    this.sliderTrack = this.container.querySelector('.homepage-icons-grid');
    this.prevButton = this.container.querySelector('.icons-slider-button--prev');
    this.nextButton = this.container.querySelector('.icons-slider-button--next');
    this.paginationDots = this.container.querySelectorAll('.icons-slider-pagination .pagination-dot');
    this.slides = this.sliderTrack.querySelectorAll('.homepage-icon-block');
    this.currentSlideIndex = 0;

    // These values are approximate and depend on CSS flex-basis and gap
    this.desktopItemsToShow = 4;
    this.tabletItemsToShow = 4;
    this.mobileItemsToShow = 2;

    this.init();
  }

  init() {
    // Only proceed if there are actual slides to manage
    if (this.slides.length === 0) {
      this.toggleSliderElements(false);
      return;
    }

    this.prevButton.addEventListener('click', this.goToPrevSlide.bind(this));
    this.nextButton.addEventListener('click', this.goToNextSlide.bind(this));
    this.sliderTrack.addEventListener('scroll', this.debounce(this.updateSliderUI.bind(this), 50));
    window.addEventListener('resize', this.debounce(this.handleResize.bind(this), 100));

    this.paginationDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    this.handleResize(); // Initial setup based on current window size
  }

  debounce(func, delay) {
    let timeout;
    return function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  }

  getSlidesPerPage() {
    const width = window.innerWidth;
    if (width <= 768) return this.mobileItemsToShow; // Mobile breakpoint
    // For tablet and desktop, all items are visible, so no "slides per page" for scrolling
    // Return a value that effectively disables the slider logic on these sizes
    return this.tabletItemsToShow; // Return 4 for tablet/desktop where it's a grid
  }

  getScrollPositionForSlide(index) {
    if (this.slides.length === 0) return 0;

    const slide = this.slides[index];
    const slideOffsetLeft = slide.offsetLeft;
    
    // Get computed gap from the track
    const style = getComputedStyle(this.sliderTrack);
    const gap = parseFloat(style.gap || '0');
    
    // For a slider that scrolls one item at a time (or by a page of items)
    // Here, we want to scroll by one item + gap for consistent behavior.
    return (slide.offsetWidth + gap) * index;
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.currentSlideIndex = index;
    const scrollPosition = this.getScrollPositionForSlide(this.currentSlideIndex);
    this.sliderTrack.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    this.updateSliderUI();
  }

  goToNextSlide() {
    const slidesPerPage = this.getSlidesPerPage();
    if (slidesPerPage > this.slides.length) return; // No scrolling needed if all fit

    let nextIndex = this.currentSlideIndex + 1;
    
    // Determine the furthest index we can scroll to while still showing 'slidesPerPage' items
    const maxScrollableIndex = Math.max(0, this.slides.length - slidesPerPage); // Ensure it's not negative

    if (nextIndex > maxScrollableIndex) {
        nextIndex = maxScrollableIndex; // Snap to the last possible position
    }
    
    this.currentSlideIndex = nextIndex;
    this.goToSlide(this.currentSlideIndex);
  }

  goToPrevSlide() {
    if (this.currentSlideIndex <= 0) return; // Already at the start
    
    let prevIndex = this.currentSlideIndex - 1;
    this.currentSlideIndex = prevIndex;
    this.goToSlide(this.currentSlideIndex);
  }

  toggleSliderElements(show) {
    // Show or hide slider buttons and pagination based on 'show' boolean
    const displayValue = show ? 'flex' : 'none'; // Buttons are flex, pagination is block
    const paginationDisplayValue = show ? 'block' : 'none';

    if (this.prevButton) this.prevButton.style.display = displayValue;
    if (this.nextButton) this.nextButton.style.display = displayValue;
    
    const paginationWrapper = this.paginationDots[0]?.closest('.icons-slider-pagination');
    if (paginationWrapper) paginationWrapper.style.display = paginationDisplayValue;

    // Adjust the track overflow-x based on slider active state
    if (this.sliderTrack) {
        this.sliderTrack.style.overflowX = show && this.slides.length > this.getSlidesPerPage() ? 'scroll' : 'hidden';
        this.sliderTrack.style.scrollSnapType = show && this.slides.length > this.getSlidesPerPage() ? 'x mandatory' : 'none';
    }
  }

  updateSliderUI() {
    if (this.slides.length === 0) return;

    const scrollLeft = this.sliderTrack.scrollLeft;
    const scrollWidth = this.sliderTrack.scrollWidth;
    const clientWidth = this.sliderTrack.clientWidth;

    const slidesPerPage = this.getSlidesPerPage();
    const isSliderActive = window.innerWidth <= 768 && this.slides.length > slidesPerPage;

    this.toggleSliderElements(isSliderActive);

    if (isSliderActive) {
      // Update arrow button states
      this.prevButton.disabled = scrollLeft <= 0;
      this.nextButton.disabled = Math.ceil(scrollLeft) + clientWidth >= scrollWidth - 1;

      // Calculate current slide based on scroll position
      const slideWidth = this.slides[0].offsetWidth;
      const gap = parseFloat(getComputedStyle(this.sliderTrack).gap || '0');
      if (slideWidth + gap > 0) {
        this.currentSlideIndex = Math.round(scrollLeft / (slideWidth + gap));
      } else {
        this.currentSlideIndex = 0;
      }

      // Update pagination dots
      this.paginationDots.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentSlideIndex);
        dot.tabIndex = index === this.currentSlideIndex ? 0 : -1;
      });
    }
  }

  handleResize() {
    const slidesPerPage = this.getSlidesPerPage();
    const isSliderActive = window.innerWidth <= 768 && this.slides.length > slidesPerPage;

    this.toggleSliderElements(isSliderActive);
    
    // Ensure scroll position resets if going from mobile slider to desktop grid
    if (!isSliderActive) {
      this.sliderTrack.scrollTo({ left: 0, behavior: 'instant' });
      this.currentSlideIndex = 0;
    }
    
    this.updateSliderUI();
  }
}